import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Supabase client
import { supabase } from './lib/supabase';

// Blueprint Context - Central configuration system
import { BlueprintProvider } from './context/BlueprintContext';

// Pages
import Landing from './pages/Landing';
import HomePage from './pages/HomePage'; // Old dynamic home page (backup)
import PremiumHomePage from './components/home/PremiumHomePage'; // Premium CMS-driven Homepage
import PremiumNavbar from './components/home/PremiumNavbar'; // Premium Navbar
import MaterialExplorer from './pages/MaterialExplorer'; // New dynamic explorer
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import DashboardNew from './pages/DashboardNew';
import { Dashboard as DynamicDashboard } from './components/dashboard';
import MaterialLibrary from './pages/MaterialLibrary';
import MaterialDetail from './pages/MaterialDetail';
import MaterialCompare from './pages/MaterialCompare';
import IdentifyStone from './pages/IdentifyStone';
import Projects from './pages/Projects';
import { ProjectsPage as ProjectsSuapabase } from './pages/projects';
import BoardDetail from './pages/BoardDetail';
import ClientReview from './pages/ClientReview';
import PresentationMode from './pages/PresentationMode';
import AdminPanel from './pages/AdminPanel';
import InspirationGallery from './pages/InspirationGallery';
import Settings from './pages/Settings';
import MyBoards from './pages/MyBoards';
// MaterialVisualSearch removed - using VisualSearchV3 only
import StoneAdvisorDashboard, { AdvisorRequestDetail } from './pages/StoneAdvisorDashboard';
import MaterialSpecification from './pages/MaterialSpecification';
import ProjectInspirationGallery, { ProjectInspirationDetail } from './pages/ProjectInspirationGallery';
import TrendsDashboard from './pages/TrendsDashboard';
import NotificationsPage from './pages/NotificationsPage';
import HotspotEditor from './pages/HotspotEditor';
import AdvisorGroupsPage from './pages/AdvisorGroupsPage';
import MyTrendingMaterials from './pages/MyTrendingMaterials';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetailPage from './pages/CollectionDetailPage';
import SimilarMaterialsPage from './pages/SimilarMaterialsPage';
import BulkImportPage from './pages/Admin/BulkImportPage';
import ClientRegister from './pages/ClientRegister';
import AdminStatistics from './pages/AdminStatistics';
import RepDashboard from './pages/RepDashboard';
import Pricing from './pages/Pricing';
import SubscriptionManagement from './pages/SubscriptionManagement';
import StudioWorkspace from './pages/StudioWorkspace';
import StudioProjectDetail from './pages/StudioProjectDetail';
import MaterialComparePage from './pages/MaterialComparePage';
import ClientApprovalReview from './pages/ClientApprovalReview';
import SupplierPortal from './pages/SupplierPortal';
import SupportCenter from './pages/SupportCenter'; // Support Center for users
import SREProjectPage from './pages/SRE/SREProjectPage';
import SRELandingPage from './pages/SRE/SRELandingPage';
import SREV2Page from './pages/SREV2Page';
import SlabCleaningDebugPage from './pages/SlabCleaningDebugPage';
import Onboarding from './pages/Onboarding';
import Logout from './pages/Logout';

// New CMS-driven pages
import ColorExperiencePage from './pages/experience/ColorExperiencePage';
import MaterialDetailLuxury from './pages/MaterialDetailLuxury';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectDetailPageNew from './pages/ProjectDetailPageNew'; // New Project Detail with Materials
import MaterialDetailPage from './pages/MaterialDetailPage'; // Phase 3: Material Intelligence
import ColorNavigation from './pages/ColorNavigation'; // Phase 3: Color Entry Point

// Phase 5: Workspace
import WorkspacePage from './pages/WorkspacePage';

// Phase 6: Blueprint Admin
import BlueprintAdminPage from './pages/BlueprintAdminPage';

// Phase 7: Surface Replacement
import SurfaceReplacement from './pages/SurfaceReplacement';

// Image Match
import ImageMatchPage from './pages/ImageMatchPage';
import DnaComparePage from './pages/DnaComparePage';
import MaterialDNACompare from './pages/MaterialDNACompare';
import VisualSearchV3 from './pages/VisualSearchV3'; // NEW: DNA-based visual search
import RetailAdvisorPage from './pages/RetailAdvisorPage'; // Retail/Fabricator/Advisor directory
import AdvisorNetworkPage from './pages/AdvisorNetworkPage'; // AIL - Advisor Intelligence Layer
import ProfessionalDashboard from './pages/ProfessionalDashboard'; // Professional Dashboard

// Inspiration/Journal CMS
import InspirationPage from './pages/InspirationPage';
import InspirationListPage from './pages/InspirationListPage';
import InspirationDetailPage from './pages/InspirationDetailPage';

// Generic CMS Page
import CMSPage from './pages/CMSPage';

// Client Proposal Page (public, no auth required)
import ClientProposalPage from './pages/ClientProposalPage';

// Proposals Management Page (for designers/architects)
import ProposalsPage from './pages/ProposalsPage';

// Proposal Workspace (premium multi-panel workspace)
import ProposalWorkspace from './pages/ProposalWorkspace';

// Render History Page
import MyRendersPage from './pages/MyRendersPage';

// Sentry Test Page (admin only)
import SentryTestPage from './pages/SentryTestPage';

import { authLog } from './utils/authUtils';
import authService from './services/authService';
import { setCachedAuthState } from './hooks/useAuth';

// ============== SUPABASE AUTH ==============
// All authentication via Supabase - NO MongoDB, NO fallbacks
export const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Check if Supabase is configured
  const isConfigured = Boolean(supabase);
  
  // CRITICAL: Timeout constants for auth operations
  const AUTH_TIMEOUT_MS = 8000;  // 8 seconds max for entire auth init
  const PROFILE_TIMEOUT_MS = 5000;  // 5 seconds max for profile fetch

  /**
   * FETCH PROFILE - With timeout protection
   * Uses authService.getCurrentProfile() which auto-creates if missing
   */
  const fetchProfile = async (userId) => {
    if (!isConfigured || !userId) {
      return null;
    }
    
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), PROFILE_TIMEOUT_MS);
      });
      
      // Create fetch promise
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      // Race between fetch and timeout
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (error) {
        // PGRST116 = profile not found - expected for new users
        if (error.code === 'PGRST116') {
          return null;
        }
        console.warn('[AUTH] fetchProfile error:', error.message);
        return null;
      }
      
      return data;
      
    } catch (err) {
      console.warn('[AUTH] fetchProfile exception:', err.message);
      return null;
    }
  };

  /**
   * INIT AUTH - On mount with TIMEOUT PROTECTION
   * CRITICAL: Will ALWAYS resolve loading = false within AUTH_TIMEOUT_MS
   */
  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      setAuthInitialized(true);
      return;
    }

    let isMounted = true;
    let timeoutId = null;
    
    // CRITICAL: Watchdog timeout - guarantees loading ends
    const setupWatchdog = () => {
      timeoutId = setTimeout(() => {
        if (isMounted && loading) {
          console.warn('[AUTH] Watchdog: Forcing loading=false after timeout');
          setLoading(false);
          setAuthInitialized(true);
          setAuthError('Auth initialization timeout');
        }
      }, AUTH_TIMEOUT_MS);
    };
    
    setupWatchdog();

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.warn('[AUTH] initAuth: Session error', error.message);
          setUser(null);
          setProfile(null);
          setAuthError(error.message);
        } else if (session?.user) {
          setUser(session.user);
          setCachedAuthState(session, session.user);
          
          // Fetch profile (with its own timeout)
          const profileData = await fetchProfile(session.user.id);
          if (isMounted) {
            setProfile(profileData);
          }
        } else {
          setUser(null);
          setProfile(null);
          setCachedAuthState(null, null);
        }
      } catch (err) {
        console.warn('[AUTH] initAuth exception:', err.message);
        if (isMounted) {
          setUser(null);
          setProfile(null);
          setAuthError(err.message);
        }
      } finally {
        if (isMounted) {
          clearTimeout(timeoutId);
          setLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    initAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        // Skip INITIAL_SESSION - handled by initAuth
        if (event === 'INITIAL_SESSION') {
          return;
        }
        
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setCachedAuthState(session, session.user);
          setAuthError(null);
          
          const profileData = await fetchProfile(session.user.id);
          if (isMounted) {
            setProfile(profileData);
            setLoading(false);
          }
          
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setCachedAuthState(null, null);
          setLoading(false);
          
        } else if (event === 'TOKEN_REFRESHED') {
          setCachedAuthState(session, session?.user);
          
        } else if (event === 'USER_UPDATED' && session?.user) {
          setUser(session.user);
          setCachedAuthState(session, session.user);
          const profileData = await fetchProfile(session.user.id);
          if (isMounted) {
            setProfile(profileData);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [isConfigured]);

  /**
   * LOGIN - Email/Password
   */
  const login = async (email, password) => {
    console.log('[AUTH] login: Starting', { email });
    
    if (!isConfigured) {
      throw new Error('Supabase not configured');
    }
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('[AUTH] login: Error', error.message);
        setLoading(false);
        throw error;
      }
      
      console.log('[AUTH] login: Success', { email: data.user?.email });
      // Profile will be loaded by onAuthStateChange
      return { user: data.user, session: data.session };
      
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  /**
   * LOGOUT
   */
  const logout = async () => {
    console.log('[AUTH] logout: Starting');
    
    try {
      if (isConfigured) {
        await supabase.auth.signOut();
      }
    } catch (err) {
      console.error('[AUTH] logout: Error', err.message);
    } finally {
      setUser(null);
      setProfile(null);
      setLoading(false);
      console.log('[AUTH] logout: Complete');
    }
    
    return true;
  };

  /**
   * REFRESH PROFILE
   */
  const refreshProfile = async () => {
    console.log('[AUTH] refreshProfile: Starting');
    
    if (!user?.id) {
      console.log('[AUTH] refreshProfile: No user');
      return null;
    }
    
    const profileData = await fetchProfile(user.id);
    setProfile(profileData);
    
    console.log('[AUTH] refreshProfile: Complete', { hasProfile: !!profileData });
    return profileData;
  };
  
  // Check if profile is complete
  const isProfileComplete = authService.isProfileComplete(profile);

  // Context value
  const value = {
    // Auth state
    user,
    profile,
    loading,
    authInitialized,
    authError,
    
    // Actions
    login,
    logout,
    refreshProfile,
    
    // Computed
    isAuthenticated: !!user,
    isAdmin: profile?.role_system === 'admin',
    isProfileComplete,
    
    // Legacy compatibility
    isSupabaseConfigured: isConfigured,
    authenticatedUser: user,
    role: profile?.role_system || null,
    plan: profile?.plan_id || null,
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route - Requires authentication AND complete profile
// HARDENED: With loading timeout watchdog and graceful failure
const ProtectedRoute = ({ children }) => {
  const { user, profile, loading, authInitialized, isProfileComplete } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // CRITICAL: Local watchdog for ProtectedRoute loading state
  // If AuthProvider's loading hangs, this catches it
  useEffect(() => {
    if (!loading) {
      setLoadingTimeout(false);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('[AUTH] ProtectedRoute: Loading timeout triggered');
        setLoadingTimeout(true);
      }
    }, 10000); // 10 second watchdog
    
    return () => clearTimeout(timeoutId);
  }, [loading]);

  // STATE 0: Loading timeout reached → Show retry UI
  if (loadingTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-amber-500/10 border-2 border-amber-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-light text-white mb-3">
            Connessione lenta
          </h2>
          <p className="text-zinc-400 mb-6 text-sm">
            La verifica dell'autenticazione sta impiegando più tempo del previsto.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-[#B08B5C] text-black font-medium rounded-lg hover:brightness-110 transition-all"
            >
              Riprova
            </button>
            <Link
              to="/login"
              className="px-5 py-2.5 bg-zinc-800 text-white font-medium rounded-lg hover:bg-zinc-700 transition-all"
            >
              Vai al Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // STATE 1: Loading → Spinner with subtle progress hint
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#B08B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/50 text-sm">Autenticazione...</p>
        </div>
      </div>
    );
  }

  // STATE 2: No user → Redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // STATE 3: No profile → Redirect to onboarding (profile will be created there)
  if (!profile) {
    return <Navigate to="/onboarding" replace />;
  }

  // STATE 4: Profile incomplete → Redirect to onboarding
  if (!isProfileComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // STATE 5: All good → Render children
  return children;
};

// Onboarding Route - Requires authentication but NOT complete profile
// HARDENED: With loading timeout protection
const OnboardingRoute = ({ children }) => {
  const { user, profile, loading, isProfileComplete } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Loading timeout watchdog
  useEffect(() => {
    if (!loading) {
      setLoadingTimeout(false);
      return;
    }
    const timeoutId = setTimeout(() => {
      if (loading) setLoadingTimeout(true);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  // Timeout fallback
  if (loadingTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-amber-500/10 border-2 border-amber-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-light text-white mb-3">Connessione lenta</h2>
          <p className="text-zinc-400 mb-6 text-sm">Riprova o torna al login.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => window.location.reload()} className="px-5 py-2.5 bg-[#B08B5C] text-black font-medium rounded-lg">Riprova</button>
            <Link to="/login" className="px-5 py-2.5 bg-zinc-800 text-white rounded-lg">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#B08B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/50 text-sm">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Profile already complete - redirect to dashboard
  if (isProfileComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin Route - HARDENED
const AdminRoute = ({ children }) => {
  const { isAdmin, loading, user } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  useEffect(() => {
    if (!loading) {
      setLoadingTimeout(false);
      return;
    }
    const timeoutId = setTimeout(() => {
      if (loading) setLoadingTimeout(true);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  if (loadingTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <p className="text-white mb-4">Timeout connessione</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-[#B08B5C] text-black rounded-lg">Riprova</button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="w-8 h-8 border-2 border-[#B08B5C] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// SRE Route - Requires can_access_sre flag or admin - HARDENED
const SRERoute = ({ children }) => {
  const { user, loading, isAdmin, profile } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  useEffect(() => {
    if (!loading) {
      setLoadingTimeout(false);
      return;
    }
    const timeoutId = setTimeout(() => {
      if (loading) setLoadingTimeout(true);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  if (loadingTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <p className="text-white mb-4">Timeout connessione</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-[#B08B5C] text-black rounded-lg">Riprova</button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#B08B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/50 text-sm">Caricamento SRE...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin always has access, otherwise check can_access_sre flag in profile
  const canAccessSRE = isAdmin || profile?.can_access_sre;
  
  if (!canAccessSRE) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-light text-white mb-2">SRE Access Required</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            SlabReality Engine è in Beta. Contatta l'amministratore per richiedere l'accesso.
          </p>
          <button 
            onClick={() => window.history.back()} 
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            Torna Indietro
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Scroll Reset Component
const ScrollReset = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollReset />
      <Routes>
      {/* PREMIUM: CMS-driven Homepage (100% database content) */}
      <Route path="/" element={<Landing />} />
      <Route path="/premium-home-old" element={<PremiumHomePage />} />
      
      {/* Legacy pages (keep for reference) */}
      <Route path="/home-old" element={<HomePage />} />
      <Route path="/landing-old" element={<Landing />} />
      
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
      <Route path="/review/:shareToken" element={<ClientReview />} />
      <Route path="/present/:shareToken" element={<PresentationMode />} />
      <Route path="/inspiration" element={<InspirationPage />} />
      <Route path="/inspiration/articles" element={<InspirationListPage />} />
      <Route path="/inspiration/:slug" element={<InspirationDetailPage />} />
      <Route path="/visual-search" element={<VisualSearchV3 />} />
      
      {/* Phase 3: Color Navigation Entry Point */}
      <Route path="/experience/colors" element={<ColorNavigation />} />
      
      {/* Phase 4: Material Detail - Luxury Tech Edition */}
      <Route path="/materials/:materialId" element={<MaterialDetailLuxury />} />
      {/* Phase 3 Material Intelligence Page - Blueprint driven */}
      <Route path="/materials-v3/:slug" element={<MaterialDetailPage />} />
      
      {/* Legacy Material Detail (keep for reference) */}
      <Route path="/materials-legacy/:materialId" element={<MaterialDetail />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DynamicDashboard /></ProtectedRoute>} />
      <Route path="/dashboard-old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard-legacy" element={<ProtectedRoute><DashboardNew /></ProtectedRoute>} />
      <Route path="/materials" element={<MaterialExplorer />} />
      <Route path="/materials-old" element={<MaterialLibrary />} />
      <Route path="/materials/:materialId/specification" element={<ProtectedRoute><MaterialSpecification /></ProtectedRoute>} />
      <Route path="/compare" element={<MaterialCompare />} />
      <Route path="/identify" element={<IdentifyStone />} />
      <Route path="/retail-advisor" element={<RetailAdvisorPage />} />
      <Route path="/advisor-network" element={<AdvisorNetworkPage />} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects-new" element={<ProtectedRoute><ProjectsSuapabase /></ProtectedRoute>} />
      <Route path="/projects/:boardId" element={<ProtectedRoute><BoardDetail /></ProtectedRoute>} />
      <Route path="/project/:projectId" element={<ProtectedRoute><ProjectDetailPageNew /></ProtectedRoute>} />
      <Route path="/project-old/:projectId" element={<ProjectDetailPage />} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/my-boards" element={<ProtectedRoute><MyBoards /></ProtectedRoute>} />
      <Route path="/stone-search" element={<Navigate to="/visual-search" replace />} />
      <Route path="/advisor" element={<ProtectedRoute><StoneAdvisorDashboard /></ProtectedRoute>} />
      <Route path="/advisor/requests/:requestId" element={<ProtectedRoute><AdvisorRequestDetail /></ProtectedRoute>} />
      
      {/* Project Inspiration Engine */}
      <Route path="/project-inspiration" element={<ProjectInspirationGallery />} />
      <Route path="/project-inspiration/:projectId" element={<ProjectInspirationDetail />} />
      
      {/* Material Trends */}
      <Route path="/trends" element={<ProtectedRoute><TrendsDashboard /></ProtectedRoute>} />
      <Route path="/trends/my-materials" element={<ProtectedRoute><MyTrendingMaterials /></ProtectedRoute>} />
      
      {/* Similar Materials (SEO Pages) */}
      <Route path="/materials/similar-to/:materialId" element={<SimilarMaterialsPage />} />
      <Route path="/materials/similar/:slug" element={<SimilarMaterialsPage />} />
      
      {/* Collections */}
      <Route path="/collections" element={<CollectionsPage />} />
      <Route path="/collections/:collectionId" element={<CollectionDetailPage />} />
      
      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* Hotspot Editor (Admin) */}
      <Route path="/project-inspiration/:projectId/edit-hotspots" element={<AdminRoute><HotspotEditor /></AdminRoute>} />
      
      {/* Legacy redirect for old board URLs */}
      <Route path="/boards" element={<Navigate to="/projects" replace />} />
      <Route path="/boards/:boardId" element={<Navigate to="/projects/:boardId" replace />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
      <Route path="/admin/advisor-groups" element={<AdminRoute><AdvisorGroupsPage /></AdminRoute>} />
      <Route path="/admin/bulk-import" element={<AdminRoute><BulkImportPage /></AdminRoute>} />
      <Route path="/admin/statistics" element={<AdminRoute><AdminStatistics /></AdminRoute>} />
      
      {/* Blueprint CMS (Has internal DEV_MODE bypass) */}
      <Route path="/blueprint" element={<BlueprintAdminPage />} />
      
      {/* Workspace (Public - works in demo mode) */}
      <Route path="/workspace" element={<WorkspacePage />} />
      
      {/* Image Match (Public) */}
      <Route path="/match" element={<VisualSearchV3 />} />
      <Route path="/dna-compare" element={<DnaComparePage />} />
      <Route path="/material-compare" element={<MaterialDNACompare />} />
      
      {/* Rep Dashboard */}
      <Route path="/rep" element={<ProtectedRoute><RepDashboard /></ProtectedRoute>} />
      <Route path="/rep/dashboard" element={<ProtectedRoute><RepDashboard /></ProtectedRoute>} />
      
      {/* Pricing & Subscription */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/account/subscription" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
      
      {/* Studio Workspace */}
      <Route path="/studio" element={<ProtectedRoute><StudioWorkspace /></ProtectedRoute>} />
      <Route path="/studio/workspace" element={<ProtectedRoute><StudioWorkspace /></ProtectedRoute>} />
      <Route path="/studio/projects/:projectId" element={<ProtectedRoute><StudioProjectDetail /></ProtectedRoute>} />
      
      {/* Material Comparison */}
      <Route path="/compare/materials" element={<MaterialComparePage />} />
      
      {/* Client Approval Review (public, token-based) */}
      <Route path="/client-review/:token" element={<ClientApprovalReview />} />
      
      {/* Client Registration via Invitation */}
      <Route path="/register/client" element={<ClientRegister />} />
      
      {/* Supplier Portal */}
      <Route path="/supplier" element={<ProtectedRoute><SupplierPortal /></ProtectedRoute>} />
      
      {/* Support Center */}
      <Route path="/support" element={<ProtectedRoute><SupportCenter /></ProtectedRoute>} />
      
      {/* Proposals Management */}
      <Route path="/proposals" element={<ProtectedRoute><ProposalsPage /></ProtectedRoute>} />
      <Route path="/proposals/:proposalId" element={<ProtectedRoute><ProposalWorkspace /></ProtectedRoute>} />
      
      {/* Render History */}
      <Route path="/my-renders" element={<ProtectedRoute><MyRendersPage /></ProtectedRoute>} />
      
      {/* Professional Dashboard (Retailers, Fabricators, Advisors) */}
      <Route path="/professional" element={<ProtectedRoute><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/dashboard" element={<ProtectedRoute><ProfessionalDashboard /></ProtectedRoute>} />
      
      {/* SRE V2 Debug Tool - Slab Cleaning Pipeline - OUTSIDE AuthProvider */}
      <Route path="/debug/sre-cleaning" element={<SlabCleaningDebugPage />} />
      
      {/* Surface Replacement Engine V2 - Premium UI */}
      <Route path="/sre-v2" element={<SREV2Page />} />
      <Route path="/surface-replacement-v2" element={<SREV2Page />} />
      
      {/* Surface Replacement Engine - Legacy React Page */}
      <Route path="/surface-replacement" element={<ProtectedRoute><SurfaceReplacement /></ProtectedRoute>} />
      
      {/* Slab Reality Engine (SRE) - Requires can_access_sre flag */}
      <Route path="/sre" element={<SRERoute><SRELandingPage /></SRERoute>} />
      <Route path="/sre/project" element={<SRERoute><SREProjectPage /></SRERoute>} />
      
      {/* SRE V2 Debug Tool - Slab Cleaning Pipeline */}
      <Route path="/sre/debug/cleaning" element={<SlabCleaningDebugPage />} />
      
      {/* Sentry Test Page - For monitoring verification */}
      <Route path="/sentry-test" element={<SentryTestPage />} />
      
      {/* NEW: Color Experience (CMS-driven, MOCK DATA) */}
      <Route path="/experience/colors" element={<ColorExperiencePage />} />
      
      {/* CMS Generic Pages (About, Contact, Privacy, Terms, etc.) */}
      <Route path="/about" element={<CMSPage />} />
      <Route path="/contact" element={<CMSPage />} />
      <Route path="/privacy" element={<CMSPage />} />
      <Route path="/terms" element={<CMSPage />} />
      
      {/* Client Proposal View - PUBLIC (no auth required) */}
      <Route path="/client/proposal/:token" element={<ClientProposalPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  // Log mount - MUST appear only ONCE
  useEffect(() => {
    console.log('🚀 App MOUNTED');
    return () => console.log('🚀 App UNMOUNTED');
  }, []);

  // CRITICAL: Provider tree is STATIC
  // AuthProvider and BlueprintProvider NEVER conditionally render
  // All conditional logic happens INSIDE AppRouter via Routes
  return (
    <AuthProvider>
      <BlueprintProvider initialLang="it">
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </BlueprintProvider>
    </AuthProvider>
  );
}

export default App;
